import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { rolesService } from "../../services/apiService";
import { showToast } from "../../utils/toast";

const ACTIONS = [
  { key: "is_view", label: "View" },
  { key: "is_add", label: "Add" },
  { key: "is_update", label: "Update" },
  { key: "is_delete", label: "Delete" },
];

export default function RolePermissions() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      rolesService.getById(id),
      rolesService.getModulesWithPermissions(id),
    ])
      .then(([roleRes, modRes]) => {
        setRole(roleRes?.data || null);
        setModules(modRes?.data || []);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const toggle = (moduleId, action) => {
    setModules((prev) =>
      prev.map((m) =>
        m.module_id === moduleId
          ? { ...m, [action]: Number(m[action]) ? 0 : 1 }
          : m
      )
    );
  };

  const toggleRow = (moduleId, value) => {
    setModules((prev) =>
      prev.map((m) =>
        m.module_id === moduleId
          ? { ...m, is_view: value, is_add: value, is_update: value, is_delete: value }
          : m
      )
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const permissions = modules.map((m) => ({
        module_id: m.module_id,
        is_view: m.is_view,
        is_add: m.is_add,
        is_update: m.is_update,
        is_delete: m.is_delete,
      }));
      const res = await rolesService.savePermissions(Number(id), permissions);
      if (res?.success) showToast("success", "Permissions saved");
      else showToast("error", res?.msg || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const roleLabel =
    role?.name === "group_admin"
      ? "Group Admin"
      : role?.name === "centre_admin"
      ? "Centre Admin"
      : role?.name;

  return (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <strong>Permissions — {roleLabel || "Role"}</strong>
        <button className="btn btn-outline-secondary" onClick={() => navigate("/roles")}>
          Back
        </button>
      </div>
      <div className="card-body">
        {loading && <p className="text-muted">Loading…</p>}
        {!loading && (
          <>
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Module</th>
                    {ACTIONS.map((a) => (
                      <th key={a.key} className="text-center">{a.label}</th>
                    ))}
                    <th className="text-center">All</th>
                  </tr>
                </thead>
                <tbody>
                  {modules.map((m) => {
                    const all =
                      m.is_view && m.is_add && m.is_update && m.is_delete;
                    return (
                      <tr key={m.module_id}>
                        <td className="fw-semibold">{m.name}</td>
                        {ACTIONS.map((a) => (
                          <td key={a.key} className="text-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={!!Number(m[a.key])}
                              onChange={() => toggle(m.module_id, a.key)}
                            />
                          </td>
                        ))}
                        <td className="text-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={!!all}
                            onChange={() => toggleRow(m.module_id, all ? 0 : 1)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save Permissions"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
